import type { ResolvedEntityOperation } from '@workbuddy/services/entities';
import type { BoardPanelPresentationState } from '../../state/board.panel.presentation';
import { boardOpensOnDialogOutlet, boardPanelPreviewIsEditor } from './board.panel.chrome';

/** Where ⤢ takes the record: the panel's own dialog frame, the inspector's edit view, or the document dialog. */
export type BoardPanelExpandKind = 'dialog' | 'inspector' | 'fd-dialog';

export interface BoardPanelExpand {
  /** Null hides ⤢. */
  readonly kind: BoardPanelExpandKind | null;
  readonly tooltip: string | null;
  /** ⤡ — a popped-out dialog that may return to the side. False on a board too narrow for a side panel. */
  readonly canDock: boolean;
}

export interface BoardPanelExpandInput {
  readonly presentation: BoardPanelPresentationState;
  readonly preview: ResolvedEntityOperation | null;
  readonly edit: ResolvedEntityOperation | null;
  /** A board outside the shell has no inspector or dialog outlet to open on. */
  readonly canOpenInspector: boolean;
  readonly canOpenDialogOutlet: boolean;
}

export const BOARD_PANEL_NO_EXPAND: BoardPanelExpand = { kind: null, tooltip: null, canDock: false };

const EXPAND_TOOLTIP: Readonly<Record<BoardPanelExpandKind, string>> = {
  'dialog': 'Open in dialog',
  'inspector': 'Open in inspector',
  'fd-dialog': 'Open document',
};

function expandTo(kind: BoardPanelExpandKind | null, canDock: boolean): BoardPanelExpand {

  return { kind, tooltip: kind ? EXPAND_TOOLTIP[kind] : null, canDock };
}

/**
 * ⤢ always means "this record, bigger". When the preview already is the editor (Task Detail) the
 * bigger view is the same surface in a dialog; otherwise it is the entity's editor (Job's Manage in
 * the inspector, a financial document in its own dialog), which is what the pen used to open.
 */
export function resolveBoardPanelExpand(input: BoardPanelExpandInput): BoardPanelExpand {

  const { presentation, preview, edit } = input;
  const popOut: BoardPanelExpandKind | null = presentation.canPopOut ? 'dialog' : null;

  // Before preview-is-editor: a document's preview and edit share a surface, yet only the dialog edits.
  if (boardOpensOnDialogOutlet(edit)) return expandTo(input.canOpenDialogOutlet ? 'fd-dialog' : null, presentation.canDock);
  if (boardPanelPreviewIsEditor(preview, edit)) return expandTo(popOut, presentation.canDock);
  if (edit && input.canOpenInspector) return expandTo('inspector', presentation.canDock);
  return expandTo(popOut, presentation.canDock);
}
